import { differenceInDays, addDays, startOfWeek, endOfWeek, eachWeekOfInterval, format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Constantes
export const CAPACITE_BRUTE_PAR_SEMAINE = 5; // 5 jours par semaine
export const JOURS_TRAVAILLES_PAR_SEMAINE = 5;
export const CONGES_ANNUELS = 25; // 25 jours de congés par an
export const SEMAINES_PAR_AN = 52; // 52 semaines par an

/**
 * Calcule la capacité brute d'une personne sur une période donnée
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {number} Nombre de jours de capacité brute (moins les congés proportionnels)
 */
export const calculateCapaciteBrutePeriode = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const weeks = eachWeekOfInterval(
    { start: startDate, end: endDate },
    { weekStartsOn: 1 } // Lundi comme premier jour
  );
  
  // Capacité théorique sans congés
  const capaciteTheorique = weeks.length * CAPACITE_BRUTE_PAR_SEMAINE;
  
  // Calcul proportionnel des congés annuels pour la période
  const proportionAnnuelle = weeks.length / SEMAINES_PAR_AN;
  const congesPeriode = CONGES_ANNUELS * proportionAnnuelle;
  
  // Capacité brute = capacité théorique - congés proportionnels
  const capaciteBrute = Math.max(0, capaciteTheorique - congesPeriode);
  
  return capaciteBrute;
};

/**
 * Calcule les congés proportionnels pour une période donnée
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {number} Nombre de jours de congés proportionnels
 */
/**
 * Calcule la capacité théorique d'une personne sur une période donnée (sans congés)
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {number} Nombre de jours de capacité théorique
 */
export const calculateCapaciteTheoriquePeriode = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const weeks = eachWeekOfInterval(
    { start: startDate, end: endDate },
    { weekStartsOn: 1 }
  );
  
  return weeks.length * CAPACITE_BRUTE_PAR_SEMAINE;
};

export const calculateCongesPeriode = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const weeks = eachWeekOfInterval(
    { start: startDate, end: endDate },
    { weekStartsOn: 1 }
  );
  
  const proportionAnnuelle = weeks.length / SEMAINES_PAR_AN;
  return CONGES_ANNUELS * proportionAnnuelle;
};

/**
 * Calcule la capacité nette d'une personne sur une période donnée
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @param {number} indiceProductivite - Indice de productivité en pourcentage (0-100)
 * @returns {number} Nombre de jours de capacité nette
 */
export const calculateCapaciteNettePeriode = (startDate, endDate, indiceProductivite = 85) => {
  const capaciteBrute = calculateCapaciteBrutePeriode(startDate, endDate);
  return (capaciteBrute * indiceProductivite) / 100;
};

/**
 * Calcule les capacités d'une personne individuelle
 * @param {Object} personne - Objet personne avec indice_productivite
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {Object} Objet avec capacités brute et nette
 */
export const calculateCapacitesPersonne = (personne, startDate, endDate) => {
  if (!personne || !personne.actif) {
    return {
      capaciteBrute: 0,
      capaciteNette: 0,
      indiceProductivite: 0,
      indiceVientDeLApi: false
    };
  }
  
  // Utiliser l'indice de productivité de l'API ou une valeur par défaut
  const indiceProductivite = personne.indice_productivite ?? 85;
  const indiceVientDeLApi = personne.indice_productivite !== undefined;
  
  const capaciteBrute = calculateCapaciteBrutePeriode(startDate, endDate);
  const capaciteNette = calculateCapaciteNettePeriode(
    startDate, 
    endDate, 
    indiceProductivite
  );
  
  // Log pour debug si l'indice ne vient pas de l'API
  if (!indiceVientDeLApi) {
    console.warn(`⚠️ Indice de productivité par défaut utilisé pour ${personne.prenom} ${personne.nom}`);
  }
  
  return {
    capaciteBrute,
    capaciteNette,
    indiceProductivite,
    indiceVientDeLApi
  };
};

/**
 * Calcule les capacités totales de l'équipe
 * @param {Array} personnes - Liste des personnes actives
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {Object} Objet avec les totaux et moyennes
 */
export const calculateCapacitesEquipe = (personnes, startDate, endDate) => {
  if (!personnes || personnes.length === 0) {
    return {
      totalCapaciteBrute: 0,
      totalCapaciteNette: 0,
      moyenneIndiceProductivite: 0,
      nombrePersonnes: 0,
      details: []
    };
  }
  
  const personnesActives = personnes.filter(p => p.actif);
  const details = personnesActives.map(personne => ({
    ...personne,
    ...calculateCapacitesPersonne(personne, startDate, endDate)
  }));
  
  const totalCapaciteBrute = details.reduce((sum, p) => sum + p.capaciteBrute, 0);
  
  const moyenneIndiceProductivite = personnesActives.length > 0 
    ? personnesActives.reduce((sum, p) => sum + (p.indice_productivite * 100 ?? 85), 0) / personnesActives.length
    : 0;
    const totalCapaciteNette = totalCapaciteBrute * (moyenneIndiceProductivite / 100);
  // Log des indices de productivité pour debug
  const indicesDebug = personnesActives.map(p => ({
    nom: `${p.prenom} ${p.nom}`,
    indice: p.indice_productivite ?? 85,
    vientDeLApi: p.indice_productivite !== undefined
  }));
  console.log('📊 Indices de productivité utilisés:', indicesDebug);
  
  return {
    totalCapaciteBrute,
    totalCapaciteNette,
    moyenneIndiceProductivite,
    nombrePersonnes: personnesActives.length,
    details
  };
};

/**
 * Calcule le nombre de semaines dans une période
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {number} Nombre de semaines
 */
export const calculateNombreSemaines = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const weeks = eachWeekOfInterval(
    { start: startDate, end: endDate },
    { weekStartsOn: 1 }
  );
  
  return weeks.length;
};

/**
 * Formate la période pour l'affichage
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin
 * @returns {string} Période formatée
 */
export const formatPeriode = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  
  const startStr = format(startDate, 'dd MMM yyyy', { locale: fr });
  const endStr = format(endDate, 'dd MMM yyyy', { locale: fr });
  
  return `${startStr} - ${endStr}`;
};

/**
 * Génère des suggestions d'amélioration de productivité
 * @param {Object} capacitesEquipe - Résultat de calculateCapacitesEquipe
 * @returns {Array} Liste de suggestions
 */
export const generateSuggestionsProductivite = (capacitesEquipe) => {
  const suggestions = [];
  
  if (capacitesEquipe.moyenneIndiceProductivite < 70) {
    suggestions.push({
      type: 'warning',
      message: 'La productivité moyenne de l\'équipe est faible (< 70%)',
      action: 'Analyser les causes et mettre en place des améliorations'
    });
  }
  
  if (capacitesEquipe.moyenneIndiceProductivite > 95) {
    suggestions.push({
      type: 'info',
      message: 'La productivité moyenne est très élevée (> 95%)',
      action: 'Vérifier que les estimations sont réalistes'
    });
  }
  
  // Détecter les écarts importants de productivité
  const indices = capacitesEquipe.details.map(p => p.indiceProductivite);
  const min = Math.min(...indices);
  const max = Math.max(...indices);
  
  if (max - min > 30) {
    suggestions.push({
      type: 'info',
      message: `Écart important de productivité dans l'équipe (${min}% - ${max}%)`,
      action: 'Identifier les facteurs de performance et partager les bonnes pratiques'
    });
  }
  
  return suggestions;
};

/**
 * Calcule la charge de travail optimale recommandée
 * @param {number} capaciteNette - Capacité nette disponible
 * @returns {Object} Recommandations de charge
 */
export const calculateChargeOptimale = (capaciteNette) => {
  return {
    chargeOptimale: Math.round(capaciteNette * 0.85), // 85% de la capacité nette
    chargeMaximale: Math.round(capaciteNette * 0.95), // 95% maximum
    margeSecurite: Math.round(capaciteNette * 0.15) // 15% de marge
  };
}; 